import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import type { ApiType } from '@labeling/api';
import { hc } from 'hono/client';
import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { SWRConfig, useSWRConfig } from 'swr';
import { useToastContext } from '../providers/toast';
import { useUserStore } from '../stores/user-store';
import { createSWRConfig } from '../swr-config';
import {
  buildAuthenticatedFetch,
  buildSandboxFetch,
  clearFinishedAuthInteraction,
} from '../utils/authenticated-fetch';

export type ApiClient = ReturnType<typeof hc<ApiType>>;

type ApiContextType = {
  client: ApiClient;
};

const ApiContext = createContext<ApiContextType | undefined>(undefined);

/**
 * SWR cache keys are role-agnostic (plain strings like 'labels', 'printers'),
 * and `revalidateOnFocus` is off, so a mounted page would keep rendering the
 * previous role's data after a role switch. Drop the whole cache and refetch
 * whenever the assumed role changes.
 *
 * Must live inside <SWRConfig> so useSWRConfig() resolves to that cache.
 */
const RoleChangeRevalidator = () => {
  const { mutate } = useSWRConfig();
  const assumedRole = useUserStore((s) => s.assumedRole);
  const previousRole = useRef(assumedRole);

  useEffect(() => {
    if (previousRole.current === assumedRole) return;
    previousRole.current = assumedRole;
    // Match every key: clear the cached data, then revalidate.
    void mutate(() => true, undefined, { revalidate: true });
  }, [assumedRole, mutate]);

  return null;
};

export const ApiProvider = ({ children }: { children: React.ReactNode }) => {
  const msal = useMsal();
  const { showToast } = useToastContext();
  const assumedRole = useUserStore((s) => s.assumedRole);
  useEffect(() => {
    if (msal.inProgress === InteractionStatus.None) clearFinishedAuthInteraction();
  }, [msal.inProgress]);
  const client = useMemo(() => {
    if (import.meta.env.MODE !== 'sandbox') {
      return hc<ApiType>(import.meta.env.VITE_API_BASE_URL, {
        fetch: buildAuthenticatedFetch(msal, import.meta.env.VITE_CLIENT_ID),
      });
    }
    // Sandbox: forward the assumed role via per-request headers.
    return hc<ApiType>(import.meta.env.VITE_API_BASE_URL, {
      fetch: buildSandboxFetch(assumedRole),
    });
  }, [msal, assumedRole]);

  return (
    <SWRConfig value={createSWRConfig(showToast)}>
      <RoleChangeRevalidator />
      <ApiContext.Provider value={{ client }}>{children}</ApiContext.Provider>
    </SWRConfig>
  );
};

export const useApi = (): { client: ApiClient } => {
  const context = useContext(ApiContext);
  if (context === undefined) {
    throw new Error('useApi must be used within an ApiProvider');
  }

  return context;
};
